/**
 * @file index.tsx
 *
 * @fileoverview Renders a paragraph row that consists of a list of logos.
 */
import React, { useRef } from 'react';
import { LogoCloudSimpleParagraph } from '~/jobiqo/types/types';
import { Main } from '~/vcl/atoms/06-layout/09-main';
import { Box } from '~/vcl/atoms/07-box';
import { Picture } from '~/vcl/atoms/09-picture';
import Slide from '~/vcl/molecules/15-slider';
import { Text } from '~/vcl/atoms/13-text/03-text';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { FlexItem } from '~/vcl/atoms/06-layout/03-flex/02-flex-item';
import { FlexItems } from '~/vcl/atoms/06-layout/03-flex';
import { drupalRelativePath } from '~/jobiqo/utils/links';
import { getFrontendTheme } from '~/jobiqo/utils/getFrontend';
import styles from './logoCloudSimple.module.scss';
import { cva } from 'class-variance-authority';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';

const sliderStyles = cva([styles.logoCloudWrapper], {
  variants: {
    multiColumn: {
      true: styles.multiColumn,
    },
  },
});

interface LogoCloudSimpleProps {
  row: LogoCloudSimpleParagraph;
  maxContainerSize?: 'small' | 'medium' | 'large' | 'xLarge';
}

const PictureWrapper = ({ logo }) => {
  const LogoPicture = (
    <Picture
      title="Logo"
      landscapeSrc={logo?.image?.url}
      alt={logo?.image?.alt ?? ''}
    />
  );

  return logo?.link ? (
    <a
      href={logo.link?.url ? drupalRelativePath(logo.link?.url) : ''}
      title={logo.link?.text ?? ''}
      target="_blank"
      rel="nofollow noreferrer"
      style={{
        display: 'block',
        width: '100%',
      }}
    >
      {LogoPicture}
    </a>
  ) : (
    LogoPicture
  );
};

function MainContainer({ children, maxContainerSize, logoCloudSettings }) {
  if (!logoCloudSettings.FULL_WIDTH) {
    return (
      <Main {...(maxContainerSize ? { size: maxContainerSize } : null)}>
        {children}
      </Main>
    );
  } else {
    return <div>{children}</div>;
  }
}

export const LogoCloudSimple = ({
  row,
  maxContainerSize,
}: LogoCloudSimpleProps) => {
  const frontendConfig = useFrontendConfig();
  const frontendTheme = getFrontendTheme(frontendConfig);
  const logoCloudSettings = frontendTheme.LANDING_PAGES.LOGO_CLOUD_SIMPLE;
  const SHOW_DOTS = logoCloudSettings.SHOW_DOTS;
  const AUTOPLAY = logoCloudSettings.AUTOPLAY;
  const SHOW_PLAY_PAUSE_BUTTON = logoCloudSettings.SHOW_PLAY_PAUSE_BUTTON;
  const SLIDES_TO_SHOW = logoCloudSettings.SLIDES_TO_SHOW;
  const LOGO_SIZE = logoCloudSettings.LOGO_SIZE;
  const ref = useRef(null);
  const sliderRows = row.sliderRows ?? 1;
  const groupedLogos: any[] = []; // fix
  for (let i = 0; i < row.logos.length; i += sliderRows) {
    groupedLogos.push(row.logos.slice(i, i + sliderRows));
  }

  const [slidesToShow, setSlidesToShow] = React.useState(SLIDES_TO_SHOW);

  React.useEffect(() => {
    const updateSlidesToShow = () => {
      if (ref.current) {
        const newSlidesToShow = Math.floor(
          (ref.current as any).clientWidth / LOGO_SIZE,
        );
        setSlidesToShow(newSlidesToShow);
      }
    };

    updateSlidesToShow();
    window.addEventListener('resize', updateSlidesToShow);

    return () => {
      window.removeEventListener('resize', updateSlidesToShow);
    };
  }, [ref, LOGO_SIZE]);

  return (
    <Box
      boxShadow="none"
      borderRadius="0px"
      marginTop={0}
      paddingTop={row.spacing ?? 11}
      paddingBottom={row.spacing ?? 11}
      backgroundColorOverride={
        row.background ? `var(--color-${row.background})` : `var(--color-white)`
      }
    >
      <MainContainer
        maxContainerSize={maxContainerSize}
        logoCloudSettings={logoCloudSettings}
      >
        {row.text && (
          <>
            <Text
              textAlign="center"
              color="gray4"
              fontSize={2}
              fontWeight="bold"
              textTransform="uppercase"
            >
              {row.text}
            </Text>
            <Spacing size="large" />
          </>
        )}
        <div
          ref={ref}
          className={sliderStyles({
            multiColumn: sliderRows > 1,
          })}
        >
          {row.display === 'DISPLAY_OPTION_SLIDER' ? (
            <Slide
              slides={groupedLogos.map((logoRow, rowIndex) => {
                return sliderRows > 1 ? (
                  <FlexItems
                    flexDirection="column"
                    gap={5}
                    className="jd-logo-slider-multicol"
                    key={rowIndex}
                  >
                    {logoRow.map((logo, index) => (
                      <PictureWrapper key={index} logo={logo} />
                    ))}
                  </FlexItems>
                ) : (
                  <FlexItem key={rowIndex} padding={[5, row.spacing ?? 11]}>
                    <PictureWrapper logo={logoRow[0]} />
                  </FlexItem>
                );
              })}
              slidesToShow={slidesToShow}
              dots={SHOW_DOTS}
              autoplay={AUTOPLAY}
              showPlayPauseButton={SHOW_PLAY_PAUSE_BUTTON}
              infinite={slidesToShow < row.logos.length / sliderRows}
              responsive={[
                {
                  breakpoint: 1024,
                  settings: {
                    slidesToShow: 4,
                  },
                },
                {
                  breakpoint: 768,
                  settings: {
                    slidesToShow: 3,
                  },
                },
                {
                  breakpoint: 500,
                  settings: {
                    slidesToShow: 1,
                    dots: false,
                  },
                },
              ]}
            />
          ) : (
            <FlexItems justifyContent="center" alignItems="center" gap={9}>
              {row.logos.map((logo, index) =>
                logo?.image.url ? (
                  <FlexItem key={index}>
                    <PictureWrapper logo={logo} />
                  </FlexItem>
                ) : null,
              )}
            </FlexItems>
          )}
        </div>
      </MainContainer>
    </Box>
  );
};

export default LogoCloudSimple;
